import { useEffect } from 'react';
// ============================================================
// PRODUCT CATALOG — single source of truth for tracking values
// All prices in Argentine Pesos (ARS).
// ============================================================
export type ProductKey = 'plus' | 'basic';
export const PRODUCTS: Record<ProductKey, {
  id: string;
  name: string;
  value: number;
  currency: 'ARS';
  checkoutUrl: string;
}> = {
  plus: {
    id: 'megapack-barbero-pro-plus',
    name: 'MEGAPACK + POTENCIADORES',
    value: 27990,
    currency: 'ARS',
    checkoutUrl: 'http://centershop.store/cart/47846920355991:1'
  },
  basic: {
    id: 'megapack-barbero-pro-basic',
    name: 'MEGAPACK BARBERO PRO',
    value: 19990,
    currency: 'ARS',
    checkoutUrl: 'http://centershop.store/cart/47846916227223:1'
  }
};
const PIXEL_ID = '4298781313716057';
// Safely access fbq if available
const getFbq = (): ((...args: any[]) => void) | null => {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return typeof w.fbq === 'function' ? w.fbq : null;
};
// ============================================================
// Tracking helpers — use these from anywhere in the app
// ============================================================
export const trackInitiateCheckout = (productKey: ProductKey) => {
  const fbq = getFbq();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  fbq('track', 'InitiateCheckout', {
    value: p.value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product',
    contents: [{
      id: p.id,
      quantity: 1,
      item_price: p.value
    }],
    num_items: 1
  });
};
export const trackPurchase = (productKey: ProductKey, overrideValue?: number) => {
  const fbq = getFbq();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  const value = typeof overrideValue === 'number' ? overrideValue : p.value;
  fbq('track', 'Purchase', {
    value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product',
    contents: [{
      id: p.id,
      quantity: 1,
      item_price: value
    }],
    num_items: 1
  });
};
export const trackViewContent = (productKey: ProductKey) => {
  const fbq = getFbq();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  fbq('track', 'ViewContent', {
    value: p.value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product'
  });
};
// ============================================================
// MetaPixel component — initializes the pixel and handles
// the Purchase return URL (?purchase=plus|basic[&value=...])
// ============================================================
export function MetaPixel() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as any;
    // Create the Meta queue only when another script has not done so already.
    if (!w.fbq) {
      const n: any = w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }

    // Initialize this exact pixel once, even if an external Meta queue exists.
    const initializedKey = `__metaPixelInitialized_${PIXEL_ID}`;
    if (!w[initializedKey]) {
      w.fbq('init', PIXEL_ID);
      w.fbq('track', 'PageView');
      w[initializedKey] = true;
    }

    // Handle Purchase return URL:
    // e.g. https://landing.com/?purchase=plus   → fires Purchase for the plus offer
    // e.g. https://landing.com/?purchase=basic  → fires Purchase for the basic offer
    // Optional: &value=27990 overrides the value (useful if the checkout sends the actual paid amount)
    try {
      const params = new URLSearchParams(window.location.search);
      const purchaseProduct = params.get('purchase') as ProductKey | null;
      if (purchaseProduct === 'plus' || purchaseProduct === 'basic') {
        const valueParam = params.get('value');
        const overrideValue = valueParam ? Number(valueParam) : undefined;
        // Defer until pixel script has had a chance to load
        const fire = () => {
          const fbq = getFbq();
          if (fbq) {
            trackPurchase(purchaseProduct, overrideValue);
            // Clean the query string so a refresh doesn't double-fire
            const url = new URL(window.location.href);
            url.searchParams.delete('purchase');
            url.searchParams.delete('value');
            window.history.replaceState({}, '', url.toString());
          } else {
            // fbq not ready yet — retry shortly
            setTimeout(fire, 300);
          }
        };
        fire();
      }
    } catch (e) {
      // no-op
    }
  }, []);
  return null;
}